import { AlertCircle, Download, GripVertical } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { AiInsightsButton, AiInsightsPanel } from './ai-insights';
import { DRAG_CANCEL_CLASS, DRAG_HANDLE_CLASS } from './dashboard-grid';

export interface WidgetInsights {
  bullets: ReactNode[];
  title?: string;
  footnote?: string;
}

interface Props {
  title: ReactNode;
  /** One line under the title: what the numbers mean, how to interact. */
  subtitle?: ReactNode;
  /** Toggles and switchers for the header's right edge. */
  controls?: ReactNode;
  /** Generated commentary. Omit to hide the Insights toggle entirely. */
  insights?: WidgetInsights;
  /** Wire to a server-side export. Omit to hide the export button. */
  onExport?: () => void;
  loading?: boolean;
  /** Message to show instead of content when the widget's data failed. */
  error?: { message: string; traceId?: string; onRetry?: () => void } | null;
  /**
   * Shown when the query succeeded but returned nothing. Presence of the prop
   * is the flag — checked with `!= null`, not truthiness, so a legitimate `0`
   * or empty string still renders as the empty state.
   */
  empty?: ReactNode;
  /**
   * `metric` draws the title as a small tracked caption, because on a metric
   * tile the NUMBER is the headline and a bold title competes with it.
   */
  variant?: 'default' | 'metric';
  /**
   * Let the title wrap instead of truncating. For a note: its title is the
   * content — a row label like "United States + Canada" cut to "United
   * States + Cana…" says nothing — and a note has no chart for a wrap to push.
   */
  wrapTitle?: boolean;
  children: ReactNode;
}

/**
 * The shell every dashboard tile renders into: header (drag handle, controls,
 * insights, export) plus a content area that owns its loading / error / empty
 * states so no tile has to re-implement them.
 *
 * The card is `overflow-hidden` and never scrolls — a tile is sized to its
 * content by its grid height. If content grows, grow the tile's `h`; adding a
 * scrollbar hides data behind an interaction nobody expects on a dashboard.
 */
export function WidgetCard({
  title,
  subtitle,
  controls,
  insights,
  onExport,
  loading = false,
  error = null,
  empty,
  variant = 'default',
  wrapTitle = false,
  children,
}: Props) {
  const [showInsights, setShowInsights] = useState(false);

  return (
    // `group/tile` is named rather than bare: the header's own controls are
    // groups too, and an unnamed `group-hover` would fire on whichever
    // ancestor happened to be nearest.
    <Card className="group/tile flex h-full min-h-0 flex-col gap-0 overflow-hidden py-0">
      {/* NO RULE UNDER THE HEADER. A tile already draws two horizontal lines —
          its own border and the report footer's — and a third directly under
          the title chopped the card into bands without separating anything a
          reader needed separated. The space does that job. */}
      <header
        className={cn(
          DRAG_HANDLE_CLASS,
          'flex items-start justify-between gap-2 px-4 pb-0',
          // A METRIC'S HEADER NEVER WRAPS. Its tile is narrow, and wrapping
          // dropped the ⓘ and the edit menu onto a row of their own, pushing
          // the number and sparkline down. The title truncates instead.
          variant === 'metric' ? 'flex-nowrap pt-2.5' : 'flex-wrap pt-3',
        )}
      >
        <div className={cn('min-w-0', (variant === 'metric' || wrapTitle) && 'flex-1')}>
          <h3
            className={cn(
              'flex',
              wrapTitle ? 'items-start' : 'items-center',
              variant === 'metric'
                ? 'font-semibold text-[11px] text-muted-foreground uppercase tracking-[0.07em]'
                : 'font-semibold text-sm tracking-[-0.01em]',
            )}
          >
            {/* THE GRIP IS AN AFFORDANCE, NOT DECORATION. It answers "can I
                move this?", which nobody asks until they reach for the tile —
                so it waits for the pointer, and for keyboard focus landing
                anywhere inside the card.

                IT ALSO TAKES NO ROOM WHILE IT WAITS. Merely fading it left a
                permanent notch of empty space in front of every title, which
                is the indent of an icon that is not there — worse than the
                icon itself. The wrapper collapses to zero width instead and
                opens to make room, so the title sits flush left when the card
                is at rest and slides over as the grip arrives.

                `w-5` is the icon's 14px plus the gap it needs; the width is
                what animates, so the slide and the fade are one movement. */}
            <span
              className="inline-flex w-0 shrink-0 overflow-hidden opacity-0 motion-safe:transition-[width,opacity] motion-safe:duration-150 group-focus-within/tile:w-5 group-focus-within/tile:opacity-100 group-hover/tile:w-5 group-hover/tile:opacity-100"
              aria-hidden
            >
              <GripVertical className="size-3.5 shrink-0 text-muted-foreground/50" />
            </span>
            {/* ONE LINE, ALWAYS. The grip opening pushes the title along, and
                on a narrow tile that was enough to wrap it onto a second line
                — so the card changed height under the cursor, which is the
                jump the collapsing slot was meant to avoid. Truncated, the
                title gives way instead of reflowing. A `wrapTitle` card (a
                note) opts out: its title is all it says. */}
            <span className={cn('min-w-0', wrapTitle ? 'break-words' : 'truncate')}>{title}</span>
          </h3>
          {subtitle ? <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p> : null}
        </div>
        {/* Controls sit inside the drag handle. `DRAG_CANCEL_CLASS` tells the
            grid to ignore drags that start here, so the toggles stay clickable
            without an event handler on a non-interactive element. */}
        <div
          className={cn(
            DRAG_CANCEL_CLASS,
            'flex shrink-0 items-center',
            // Two small icons side by side: the buttons' own hit areas already
            // space them, so a gap on top read as two unrelated controls.
            variant === 'metric' ? '-mr-1.5 -mt-1 gap-0' : 'flex-wrap gap-1.5',
          )}
        >
          {controls}
          {onExport ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-7"
              onClick={onExport}
              aria-label="Export this widget"
            >
              <Download className="size-3.5" aria-hidden />
            </Button>
          ) : null}
          {insights ? (
            <AiInsightsButton active={showInsights} onClick={() => setShowInsights((v) => !v)} />
          ) : null}
        </div>
      </header>

      {/* Less room above than below: the header no longer draws a rule, so
          this padding is the only thing between the title and the content and
          a full 16px there reads as drift rather than separation. */}
      <div
        className={cn(
          'flex min-h-0 flex-1 gap-3 px-4',
          // A metric is one figure and a sparkline; the chart tiles' breathing
          // room reads as empty space around it.
          variant === 'metric' ? 'pt-1 pb-3' : 'pt-3 pb-4',
        )}
      >
        <div className="widget-main flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <WidgetBody loading={loading} error={error} empty={empty}>
            {children}
          </WidgetBody>
        </div>
        {insights && showInsights ? (
          <AiInsightsPanel
            title={insights.title}
            bullets={insights.bullets}
            footnote={insights.footnote}
            onClose={() => setShowInsights(false)}
          />
        ) : null}
      </div>
    </Card>
  );
}

function WidgetBody({
  loading,
  error,
  empty,
  children,
}: Pick<Props, 'loading' | 'error' | 'empty' | 'children'>) {
  if (loading) {
    // Mirrors the eventual shape (a chart body under a couple of value rows)
    // so the tile doesn't jump when real content arrives.
    return (
      <div className="flex flex-1 flex-col gap-3" aria-busy="true">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="min-h-24 flex-1" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
        <AlertCircle className="text-destructive" aria-hidden />
        <p className="text-sm">{error.message}</p>
        {error.traceId ? (
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {error.traceId}
          </code>
        ) : null}
        {error.onRetry ? (
          <Button type="button" size="sm" variant="outline" onClick={error.onRetry}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  if (empty != null) {
    return (
      <div className="flex flex-1 items-center justify-center text-center text-muted-foreground text-sm">
        {empty}
      </div>
    );
  }

  return <>{children}</>;
}
